import { useEffect } from 'react';
import { Box, styled } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LogoImage from '@/assets/images/findies-logo-dark.png';
import {
  ConfirmCodeStep,
  ConvertStep,
  CreateAccountStep,
  CustomizeFeedStep,
  FavoriteStoresStep,
  FrequencyStep,
  GetStartedStep,
  InvalidStep,
  LinkBankStep,
  PersonalizeStep,
  PersonalizingStep,
  PhoneNumberStep,
  PotentialRewardStep,
  RewardsStep,
  ShoppersStoriesStep,
  ShoppingStoriesStep,
  SpendingStep,
  YouDidStep,
} from '@/components/survey';
import ProgressiveLine from '@/components/ProgressiveLine';
import analytics from '@/utils/analytics/analyticsInstance';
import { useAppSelector } from '@/store/hooks';
import { selectIsUserFromMobile } from '@/store/reducers/user/userSlice';

const SURVEY_STEPS = [
  GetStartedStep,
  FrequencyStep,
  SpendingStep,
  PotentialRewardStep,
  RewardsStep,
  YouDidStep,
  ConvertStep,
  PersonalizeStep,
  CustomizeFeedStep,
  ShoppersStoriesStep,
  FavoriteStoresStep,
  PersonalizingStep,
  ShoppingStoriesStep,
  RewardsStep,
  CreateAccountStep,
  PhoneNumberStep,
  ConfirmCodeStep,
  RewardsStep,
  LinkBankStep,
];

const HIDDEN_HEADER_STEPS = [LinkBankStep];
const HIDDEN_PROGRESS_LINE_STEPS = [ShoppingStoriesStep];

const Container = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
}));

const Header = styled('header')(({ theme }) => ({
  padding: 16,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderBottomWidth: 1,
  borderBottomColor: theme.palette.border.main,
  borderBottomStyle: 'solid',
}));

const Logo = styled('img')(() => ({
  width: 90,
  height: 20,
}));

export default function SurveyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stepParam = Number(searchParams.get('step'));
  const currentStepIndex = isNaN(stepParam) ? 0 : stepParam;
  const userFromMobile = useAppSelector(selectIsUserFromMobile);
  const steps = SURVEY_STEPS;
  const isValidStep =
    Number.isInteger(currentStepIndex) && currentStepIndex >= 0 && currentStepIndex < steps.length;
  const StepComponent = isValidStep ? steps[currentStepIndex] : InvalidStep;
  const showProgress =
    !HIDDEN_PROGRESS_LINE_STEPS.includes(StepComponent) && !userFromMobile && isValidStep;

  useEffect(() => {
    analytics.track('webOnboardingScreenOpened', { screen_title: 'Survey' });

    const handlePopState = () => {
      navigate(1);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  return (
    <Container>
      {!HIDDEN_HEADER_STEPS.includes(StepComponent) && (
        <Box gap={2} display="flex" flexDirection="column">
          <Header>
            <Logo src={LogoImage} alt="Findies" />
          </Header>
          {showProgress && <ProgressiveLine totalSteps={steps.length} />}
        </Box>
      )}
      <StepComponent />
    </Container>
  );
}
